import type { Item } from '@/api/gen/_model/item';

/**
 * ORDER(순서 맞추기) 보기 순서 규칙 — **단일 정본**.
 *
 * 폼과 서버의 계약이 서로 반대 방향이라, 이 변환이 양쪽 경계에 모두 필요하다.
 * - 폼(`QuizOrderDataSchema`): `items` **배열 순서 자체가 정답 순서**다. 별도 정답 필드가 없다.
 * - 서버(`OrderSet`): `items` + `correctOrderIds` 로 나뉜다.
 *
 * 그래서 저장할 땐 `items.map(item => item.id)` 로 정답을 파생시키고,
 * 불러올 땐 이 함수로 `correctOrderIds` 순서에 맞춰 배열을 되돌린다.
 *
 * UI 표시 계층(`OrderItemList`)도 같은 규칙을 쓰므로 여기 한 곳에 둔다 —
 * 표면마다 정렬을 다시 구현하면 한쪽만 어긋났을 때 조용히 오답이 된다.
 *
 * @see views/build/util/buildFormToApiTransformer.ts (폼 → 서버)
 * @see views/build/util/apiAdapter.ts (서버 → 폼)
 */

/**
 * `items` 를 `orderIds` 가 지정한 순서로 재배열한다.
 *
 * `orderIds` 가 비어 있으면(정답 미공개 등) 원본 배열 순서를 그대로 쓴다.
 * `orderIds` 에 없는 항목은 **버리지 않고** 뒤에 원본 순서로 이어 붙인다 —
 * 데이터가 어긋났을 때 보기가 조용히 사라지는 편보다 더 보이는 편이 안전하다.
 */
export const sortItemsByOrderIds = (
  items: readonly Item[],
  orderIds?: readonly string[],
): Item[] => {
  if (!orderIds?.length) return [...items];

  const itemById = new Map(items.map(item => [item.id, item]));
  const ordered = orderIds.flatMap(id => {
    const item = itemById.get(id);
    if (!item) return [];

    itemById.delete(id);
    return [item];
  });

  return [...ordered, ...itemById.values()];
};
